import { Box, Button, Grid, Stack } from "@mui/material";
import { AlertsContext } from "@sista/dista-ui";
import { IdmClientContext } from "@sista/idm-client";
import {
  Organization,
  Relation,
  RelationDTO,
  SistaId,
  SmartButton,
  useMaxLengthValidation,
  useMinLengthValidation,
  useRequiredValidation,
  useSafeSistaAsync,
  useTransformedIcoValidation,
} from "@sista/library-stasi";
import { useIcoValidation } from "@sista/library-stasi/dist/components/form/validations/useIcoValidation";
import { SistaChip } from "@sista/sista-chip";
import { t } from "i18next";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import {
  CheckboxElement,
  FormContainer,
  TextFieldElement,
} from "react-hook-form-mui";
import { useNavigate } from "react-router-dom";

import { OrganizationRelationToOrganizationFounded } from "./OrganizationRelationToOrganizationFounded";
import { useRejstrikClient } from "../../../api/utils/useRejstrikClient";

export type OrganizationRegistrationForm = {
  idNumber: string;
  isCzechCompany: boolean;
  isForeignCompany: boolean;
};

export type OrganizationSearchByIcoContentProps = {
  onFoundOrganization: (organization: Organization) => void;
  onFoundRelationsInOrganization: (relations: RelationDTO[]) => void;
  isForeignCompanyChecked: (isForeignCompany: boolean) => void;
  onBack?: () => void;
};

export const OrganizationSearchByIcoContent = (
  props: OrganizationSearchByIcoContentProps
) => {
  const ico = useIcoValidation();
  const transformedIco = useTransformedIcoValidation();
  const maxLength = useMaxLengthValidation(8);
  const minLength = useMinLengthValidation(8);
  const required = useRequiredValidation();

  const { showError } = useContext(AlertsContext);

  const form = useForm<OrganizationRegistrationForm, unknown>({
    defaultValues: {
      idNumber: "",
      isCzechCompany: false,
      isForeignCompany: false,
    },
  });

  const ic = form.watch("idNumber");
  const urnIc = useMemo(() => {
    return new SistaId("organizations:ico", ic).asUrn();
  }, [ic]);
  const isCzechCompany = form.watch("isCzechCompany");
  const isForeignCompany = form.watch("isForeignCompany");

  const { loggedAccount, switchProfile } = useContext(IdmClientContext);

  const navigate = useNavigate();

  const onSwitchProfile = useCallback(
    (profileId: string) => {
      switchProfile(profileId);
      navigate("/");
    },
    [switchProfile, navigate]
  );

  const [selectedOrganization, setSelectedOrganization] =
    useState<Organization>(null);

  const {
    getOrCreateOrganizationByIcoWithForceRefresh,
    getMyRelationsInOrganization,
    hasLoggedAnyOrganizationRelation,
  } = useRejstrikClient();

  const [
    getOrCreateOrganizationByIcoWithForceRefreshCall,
    getOrCreateOrganizationByIcoWithForceRefreshState,
  ] = useSafeSistaAsync(getOrCreateOrganizationByIcoWithForceRefresh);

  const [getMyRelationsInOrganizationCall, getMyRelationsInOrganizationState] =
    useSafeSistaAsync(getMyRelationsInOrganization);

  const [
    hasLoggedAnyOrganizationRelationCall,
    hasLoggedAnyOrganizationRelationState,
  ] = useSafeSistaAsync(hasLoggedAnyOrganizationRelation);

  const [showDialogToSwitchProfile, setShowDialogToSwitchProfile] =
    useState(false);

  const [canContinue, setCanContinue] = useState(false);

  const submitForm = useCallback(
    async (formData: OrganizationRegistrationForm) => {
      if (formData.isCzechCompany) {
        const result = await getOrCreateOrganizationByIcoWithForceRefreshCall({
          ico: formData.idNumber,
          forceRefresh: true,
        });
        console.log("result", result);
        if (result == null) {
          return showError(
            t("ucet.organization.form.notFound", "Organizace nebyla nalezena")
          );
        }
        if (typeof result === "object" && result) {
          const profileIdWithRelationToOrganization =
            await hasLoggedAnyOrganizationRelationCall({
              organizationId: result.id,
            });
          if (profileIdWithRelationToOrganization != null) {
            setShowDialogToSwitchProfile(true);
            return;
          }
        }
        setCanContinue(true);
        return result;
      }
      if (formData.isForeignCompany) {
        props.isForeignCompanyChecked(false);
        props.onFoundRelationsInOrganization([]);
        return props.onFoundOrganization({
          legalFormCode: "26",
        } as Organization);
      }
      return null;
    },
    [
      getOrCreateOrganizationByIcoWithForceRefreshCall,
      hasLoggedAnyOrganizationRelationCall,
      props,
      showError,
    ]
  );

  useEffect(() => {
    if (!canContinue) return;
    if (isCzechCompany) {
      if (getOrCreateOrganizationByIcoWithForceRefreshState.value != null) {
        getMyRelationsInOrganizationCall({
          organizationId:
            getOrCreateOrganizationByIcoWithForceRefreshState.value.id,
        });
      }
    }
  }, [
    getMyRelationsInOrganizationCall,
    getOrCreateOrganizationByIcoWithForceRefreshState.value,
    isCzechCompany,
    canContinue,
  ]);

  useEffect(() => {
    if (!canContinue) return;
    if (getMyRelationsInOrganizationState.value != null) {
      if (isCzechCompany) {
        props.onFoundOrganization(
          getOrCreateOrganizationByIcoWithForceRefreshState.value
        );
        props.isForeignCompanyChecked(true);
      }
      props.onFoundRelationsInOrganization(
        getMyRelationsInOrganizationState.value
      );
    }
  }, [
    isCzechCompany,
    isForeignCompany,
    getMyRelationsInOrganizationState.value,
    getOrCreateOrganizationByIcoWithForceRefreshState.value,
    props,
    canContinue,
  ]);

  return (
    <FormContainer onSuccess={submitForm} formContext={form}>
      {showDialogToSwitchProfile &&
        hasLoggedAnyOrganizationRelationState.value != null && (
          <OrganizationRelationToOrganizationFounded
            profileId={hasLoggedAnyOrganizationRelationState.value.value}
            authToken={loggedAccount.authKey}
            onSwitchProfile={(profileId) => {
              setShowDialogToSwitchProfile(false);
              onSwitchProfile(profileId);
            }}
            onClose={() => setShowDialogToSwitchProfile(false)}
          />
        )}
      <Stack direction="column" spacing={2} mt={2}>
        <CheckboxElement
          name="isCzechCompany"
          label={t("ucet.organization.form.isCzechCompany", "Organizace z ČR")}
          onChange={(e) => {
            if (e.target.checked) {
              return form.setValue("isForeignCompany", !e.target.checked);
            }
          }}
        />
        {isCzechCompany && (
          <Grid container spacing={1} sx={{ alignItems: "center" }}>
            <Grid item xs={12} sm={4}>
              <TextFieldElement
                name={"idNumber"}
                autoComplete={"off"}
                label={t("ucet.organization.form.idNumber", "IČ")}
                validation={{
                  ...required,
                  //...maxLength,
                  ...minLength,
                  ...transformedIco,
                }}
              />
            </Grid>
            {ic &&
              ic.length > 0 &&
              transformedIco.validate(ic) == undefined && (
                <Grid item xs={12} sm={8}>
                  <SistaChip
                    urn={urnIc}
                    authToken={loggedAccount.authKey}
                    variant="small"
                  />
                </Grid>
              )}
          </Grid>
        )}
        <CheckboxElement
          name="isForeignCompany"
          label={t(
            "ucet.organization.form.isForeignCompany",
            "Organizace ze zahraničí"
          )}
          onChange={(e) => {
            if (e.target.checked) {
              return form.setValue("isCzechCompany", !e.target.checked);
            }
          }}
        />
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ marginTop: 4 }}
        >
          <Box>
            {props.onBack && (
              <Button variant="text" onClick={props.onBack}>
                {t("ucet.organization.form.back", "Zpět")}
              </Button>
            )}
          </Box>
          <SmartButton
            state={getOrCreateOrganizationByIcoWithForceRefreshState}
            disabled={!isCzechCompany && !isForeignCompany}
          >
            {isForeignCompany
              ? t("common.continue", "Pokračovat")
              : t("ucet.organization.form.search", "Vyhledat organizaci")}
          </SmartButton>
        </Stack>
      </Stack>
    </FormContainer>
  );
};
